import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import {
  acknowledgeUserAttention,
  ChatAttentionReason,
  ChatBackend,
  GaiaEventType,
  getSigningAttentionLink,
  searchUserAttention,
  WebsocketSubscriptionTarget,
  type GaiaEvent,
} from '@/api';
import {
  isChatAttention,
  type ChatAttentionItem,
  type SigningAttentionItem,
  type UserAttentionItem,
} from '@/components/design/domain/attention/attention';
import { WEBSOCKET_DISABLED_KEY } from '@/composables/useReviewWebSocket';
import { useGaiaToast } from '@/composables/useGaiaToast';
import { getWsClient } from '@/services/WebSocketService';
import { useAuthStore } from '@/stores/auth';
import { logger } from '@/utils/logger';

/** How many items the store holds — the API's page maximum. */
const FETCH_LIMIT = 50;

interface AttentionEventPayload {
  action: 'upserted' | 'resolved';
  attentionUid: string;
}

/**
 * Narrow the nested `UserAttention` payload out of a generic event frame:
 * `data` is the envelope (whose `uid` is the user's own uid — a synthetic
 * routing key) and `data.data` says what happened to which item. The item
 * itself is not in the event; `POST /attention/search` returns it.
 */
function asAttentionEvent(data: GaiaEvent['data']): AttentionEventPayload | null {
  if (typeof data !== 'object' || !('data' in data)) {
    return null;
  }
  const inner = (data as { data?: unknown }).data;
  if (typeof inner !== 'object' || inner === null) {
    return null;
  }
  const { action, attention_uid: attentionUid } = inner as Record<string, unknown>;
  if ((action !== 'upserted' && action !== 'resolved') || typeof attentionUid !== 'string') {
    return null;
  }
  return { action, attentionUid };
}

/**
 * Things waiting on the user — chat runs that settled while they were away and
 * signing requests where it is their turn. The backend list spans every
 * organisation the user is in; this store holds the **active organisation's**
 * slice only, because that is all the home preview and the chat badges show.
 * The cross-organisation table (ActionRequiredView) searches on its own.
 *
 * Chat runs execute on a Celery worker, so they outlive the view that started
 * them — which is why the websocket handler lives here rather than in a
 * component-scoped composable.
 *
 * `POST /attention/search` is the source of truth. The live event is a
 * shortcut, and deliberately best-effort: the socket self-closes once it has
 * been idle with no subscriptions and does not reconnect, so events can be
 * missed. Refetching whenever the socket (re)opens reconciles that without
 * keeping it alive.
 */
export const useUserAttentionStore = defineStore('userAttention', () => {
  const authStore = useAuthStore();
  const toast = useGaiaToast();

  const items = ref<UserAttentionItem[]>([]);

  /** All of the active organisation's items — `items` stops at FETCH_LIMIT. */
  const total = ref(0);

  /**
   * Whether the first fetch has settled. Lets views tell "nothing needs
   * attention" apart from "still loading" before showing an empty state.
   */
  const loaded = ref(false);

  /**
   * The thread currently on screen in a chat view, if any. A run that settles
   * while its own thread is open needs no badge anywhere — the user is watching
   * the result land.
   */
  const activeThreadUid = ref<string | null>(null);

  const chatItems = computed(() => items.value.filter(isChatAttention));

  /**
   * Only general-chat threads: they are the ones the badged sidebar entry
   * ("GAIA Chat") can actually open. Templating lives in the Office add-in and
   * review-project threads inside their project page.
   */
  const generalCount = computed(
    () => chatItems.value.filter(item => item.backend === ChatBackend.GENERAL).length
  );

  function reasonFor(threadUid: string): ChatAttentionReason | null {
    return chatItems.value.find(item => item.thread_uid === threadUid)?.reason ?? null;
  }

  // Non-reactive bookkeeping for the app-lifetime websocket registrations.
  let initialized = false;
  let seenFirstOpen = false;
  const disposers: (() => void)[] = [];

  function removeItem(uid: string): void {
    const before = items.value.length;
    items.value = items.value.filter(item => item.uid !== uid);
    total.value = Math.max(0, total.value - (before - items.value.length));
  }

  async function fetchAttention(): Promise<void> {
    // Switching organisation reloads the app, so the uid read here holds for
    // the store's lifetime. An org-less user has nothing to show.
    const organisationUid = authStore.backendUser?.active_organisation?.uid;
    if (!organisationUid) {
      items.value = [];
      total.value = 0;
      loaded.value = true;
      return;
    }
    try {
      const response = await searchUserAttention({
        body: { organisation_uids: [organisationUid], limit: FETCH_LIMIT },
      });
      items.value = response.data?.results ?? [];
      total.value = response.data?.result_count ?? 0;
      consumeActiveThread();
    } catch (error) {
      // A badge is not worth a toast — the next socket open retries.
      logger.error('Failed to load user attention:', error);
    } finally {
      loaded.value = true;
    }
  }

  /**
   * The user is watching the active thread settle, so drop its item locally
   * and, for DONE, consume it server-side too — otherwise the next fetch (a
   * reload) resurrects the badge. A failure or an unanswered interrupt still
   * deserves its badge if the user navigates away without acting on it.
   */
  function consumeActiveThread(): void {
    const uid = activeThreadUid.value;
    if (!uid) return;
    const item = chatItems.value.find(candidate => candidate.thread_uid === uid);
    if (!item) return;
    removeItem(item.uid);
    if (item.reason === ChatAttentionReason.DONE) {
      void acknowledge(item);
    }
  }

  /** Best-effort: on failure the item simply comes back on the next fetch. */
  async function acknowledge(item: ChatAttentionItem): Promise<void> {
    try {
      await acknowledgeUserAttention({ path: { attention_uid: item.uid } });
    } catch (error) {
      logger.error('Failed to acknowledge user attention:', error);
    }
  }

  /**
   * Opens the provider page where the user signs or approves. The link is
   * fetched fresh on every click (the provider expires them), and needs no
   * organisation switch — the endpoint only checks the item is the caller's.
   * The tab is opened before the request so the popup blocker still sees the
   * click that caused it.
   */
  async function openSigningLink(item: SigningAttentionItem): Promise<void> {
    const tab = window.open('', '_blank');
    try {
      const response = await getSigningAttentionLink({
        path: { attention_uid: item.uid },
        throwOnError: true,
      });
      if (tab) {
        tab.opener = null;
        tab.location.href = response.data.url;
      } else {
        window.open(response.data.url, '_blank', 'noopener,noreferrer');
      }
    } catch (error) {
      tab?.close();
      logger.error('Failed to fetch signing link:', error);
      toast.error('Could not open the signing page. Please try again.');
    }
  }

  function onAttentionEvent(event: GaiaEvent): void {
    if (event.gaia_event_type !== GaiaEventType.USER_ATTENTION) {
      return;
    }
    const payload = asAttentionEvent(event.data);
    if (!payload) {
      logger.warn('[userAttention] Unexpected attention payload');
      return;
    }
    if (payload.action === 'resolved') {
      removeItem(payload.attentionUid);
      return;
    }
    // The event names the item but does not carry it.
    void fetchAttention();
  }

  function initialize(): void {
    if (initialized) {
      return;
    }
    initialized = true;

    if (localStorage.getItem(WEBSOCKET_DISABLED_KEY) !== 'true') {
      const wsClient = getWsClient();
      // Attention events need no uid subscription — the backend auto-subscribes
      // every connection to the user's own channel.
      disposers.push(
        wsClient.registerHandler(WebsocketSubscriptionTarget.USER_ATTENTION, onAttentionEvent),
        wsClient.addStatusListener(status => {
          if (status !== 'open') {
            return;
          }
          // The boot fetch below covers the first open; later ones mean the
          // socket came back and may have missed events while it was gone.
          if (!seenFirstOpen) {
            seenFirstOpen = true;
            return;
          }
          void fetchAttention();
        })
      );
    }

    void fetchAttention();
  }

  /** A thread the owner opened (or started a new run on) is no longer waiting. */
  function clearThread(threadUid: string): void {
    for (const item of chatItems.value) {
      if (item.thread_uid === threadUid) removeItem(item.uid);
    }
  }

  /** Called by chat views as they open, switch, or leave a thread. */
  function setActiveThread(uid: string | null): void {
    activeThreadUid.value = uid;
    if (uid) clearThread(uid);
  }

  function reset(): void {
    for (const dispose of disposers.splice(0)) dispose();
    items.value = [];
    total.value = 0;
    loaded.value = false;
    activeThreadUid.value = null;
    initialized = false;
    seenFirstOpen = false;
  }

  return {
    items,
    total,
    loaded,
    chatItems,
    generalCount,
    reasonFor,
    initialize,
    fetchAttention,
    acknowledge,
    openSigningLink,
    clearThread,
    setActiveThread,
    reset,
  };
});
